import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

/**
 * 2026-10-25: Tekstfelt med forslag - i stedet for browserens <datalist>, som
 * Safari tegner som en grå boks ved siden af feltet (og Chrome forskelligt).
 * Fri tekst er altid tilladt; forslagene filtreres, mens man skriver.
 * Tastatur: pil op/ned, Enter vælger, Esc lukker.
 *
 * options: ["Pap", ...] eller [{ value, hint }]
 *
 * 2026-10-28: Listen tegnes oven på siden (portal, fast position) - før lå den inde i kortet,
 * og et kort med skjult overløb klippede den, så man kun så kanten og måtte scrolle i den.
 * Er der ikke plads under feltet, åbner den opad.
 */
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function SuggestInput({ value, onChange, options = [], placeholder, className = "", testid, emptyText, maxShown = 60, ...rest }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);     // filtrér først, når der skrives
  const [hi, setHi] = useState(-1);
  const wrap = useRef(null);
  const listRef = useRef(null);
  const [pos, setPos] = useState(null);   // {left, width, top|bottom, maxHeight}
  const opts = useMemo(() => options.map(o => (typeof o === "string" ? { value: o } : o)).filter(o => o.value), [options]);
  const shown = useMemo(() => {
    const q = norm(value);
    const list = typed && q ? opts.filter(o => norm(o.value).includes(q)) : opts;
    return list.slice(0, maxShown);
  }, [opts, value, typed, maxShown]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 8, above = r.top - 8;
      const up = below < 200 && above > below;
      setPos({ left: r.left, width: r.width, maxHeight: Math.max(120, Math.min(256, up ? above : below)),
        ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrap.current && !wrap.current.contains(e.target) && !(listRef.current && listRef.current.contains(e.target))) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("touchstart", close); };
  }, [open]);
  useEffect(() => {
    if (hi >= 0 && listRef.current) listRef.current.children[hi]?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const pick = (v) => { onChange(v); setOpen(false); setTyped(false); setHi(-1); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi(h => Math.min(shown.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && open && hi >= 0 && shown[hi]) { e.preventDefault(); pick(shown[hi].value); }
    else if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); }
    else if (e.key === "Tab") setOpen(false);
  };

  return (
    <div ref={wrap} className="relative flex-1 min-w-0">
      <input {...rest} className={`${className} pr-9`} value={value} placeholder={placeholder} autoComplete="off"
        role="combobox" aria-expanded={open} aria-autocomplete="list" data-testid={testid}
        onFocus={() => { setOpen(true); setTyped(false); setHi(-1); }}
        onChange={(e) => { onChange(e.target.value); setTyped(true); setOpen(true); setHi(-1); }}
        onKeyDown={onKey} />
      {opts.length > 0 && (
        <button type="button" tabIndex={-1} aria-label="Vis forslag" onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setOpen(o => !o); setTyped(false); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && pos && (shown.length > 0 || (typed && emptyText)) && createPortal(
        <ul ref={listRef} role="listbox" data-testid={testid ? `${testid}-options` : undefined}
          style={{ position: "fixed", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
          className="z-[1200] overflow-auto rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1">
          {shown.map((o, i) => (
            <li key={o.value} role="option" aria-selected={i === hi}
              onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o.value)} onMouseEnter={() => setHi(i)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-3 ${i === hi ? "bg-emerald-50 dark:bg-emerald-900/30" : ""} ${o.value === value ? "font-semibold text-emerald-800 dark:text-emerald-300" : "text-slate-700 dark:text-slate-200"}`}>
              <span className="truncate">{o.value}</span>
              {o.hint && <span className="text-[11px] text-slate-400 shrink-0">{o.hint}</span>}
            </li>
          ))}
          {!shown.length && typed && emptyText && <li className="px-3 py-2 text-xs text-slate-400">{emptyText}</li>}
        </ul>, document.body
      )}
    </div>
  );
}
